import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import type { Reminder, ReminderUrgency } from '@/services/reminders.service';
import { useTranslation } from 'react-i18next';

interface ReminderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    title: string;
    description: string;
    reminderDate: Date;
    urgency: ReminderUrgency;
    color: string;
    notifyBeforeMinutes: number;
  }) => void;
  initialData?: Reminder;
  selectedDate?: Date;
  isLoading?: boolean;
}

const URGENCY_OPTIONS: { value: ReminderUrgency; labelKey: string; label: string; color: string }[] = [
  { value: 'low', labelKey: 'domain:priority.low', label: 'Niedrig', color: '#10b981' },
  { value: 'medium', labelKey: 'domain:priority.medium', label: 'Mittel', color: '#3b82f6' },
  { value: 'high', labelKey: 'domain:priority.high', label: 'Hoch', color: '#f59e0b' },
  { value: 'urgent', labelKey: 'domain:priority.urgent', label: 'Dringend', color: '#ef4444' },
];

const COLOR_OPTIONS = [
  { value: '#ef4444', labelKey: 'common:opt.colRed', label: 'Rot' },
  { value: '#f59e0b', labelKey: 'common:opt.colOrange', label: 'Orange' },
  { value: '#eab308', labelKey: 'common:opt.colYellow', label: 'Gelb' },
  { value: '#10b981', labelKey: 'common:opt.colGreen', label: 'Grün' },
  { value: '#3b82f6', labelKey: 'common:opt.colBlue', label: 'Blau' },
  { value: '#8b5cf6', labelKey: 'common:opt.colPurple', label: 'Lila' },
  { value: '#ec4899', labelKey: 'common:opt.colPink', label: 'Pink' },
  { value: '#6b7280', labelKey: 'common:opt.colGray', label: 'Grau' },
];

const NOTIFY_OPTIONS = [
  { value: 0, labelKey: 'common:opt.notifyNone', label: 'Keine Benachrichtigung' },
  { value: 15, labelKey: 'common:opt.notifyMin', label: '15 Minuten vorher', labelOpts: { count: 15 } },
  { value: 30, labelKey: 'common:opt.notifyMin', label: '30 Minuten vorher', labelOpts: { count: 30 } },
  { value: 60, labelKey: 'common:opt.notifyHour1', label: '1 Stunde vorher' },
  { value: 120, labelKey: 'common:opt.notifyHours', label: '2 Stunden vorher', labelOpts: { count: 2 } },
  { value: 1440, labelKey: 'common:opt.notifyDay1', label: '1 Tag vorher' },
  { value: 2880, labelKey: 'common:opt.notifyDays', label: '2 Tage vorher', labelOpts: { count: 2 } },
];

export default function ReminderDialog({
  open,
  onOpenChange,
  onSubmit,
  initialData,
  selectedDate,
  isLoading = false,
}: ReminderDialogProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reminderDate, setReminderDate] = useState<Date | undefined>(undefined);
  const [urgency, setUrgency] = useState<ReminderUrgency>('medium');
  const [color, setColor] = useState('#3b82f6');
  const [notifyBeforeMinutes, setNotifyBeforeMinutes] = useState(15);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Initialize form with initial data or selected date
  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      setDescription(initialData.description || '');
      setReminderDate(new Date(initialData.reminder_date));
      setUrgency(initialData.urgency || 'medium');
      setColor(initialData.color || '#3b82f6');
      setNotifyBeforeMinutes(initialData.notify_before_minutes ?? 15);
    } else if (selectedDate) {
      setReminderDate(selectedDate);
    }
  }, [initialData, selectedDate]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        if (!initialData) {
          setTitle('');
          setDescription('');
          setReminderDate(selectedDate);
          setUrgency('medium');
          setColor('#3b82f6');
          setNotifyBeforeMinutes(15);
        }
      }, 200);
    }
  }, [open, initialData, selectedDate]);

  const handleDateSelect = (date: Date | undefined) => {
    setReminderDate(date);
    setCalendarOpen(false); // Close popover after selection
  };

  const handleSubmit = () => {
    if (!reminderDate) return;

    onSubmit({
      title: title.trim() || '', // Title artık opsiyonel
      description: description.trim(),
      reminderDate,
      urgency,
      color,
      notifyBeforeMinutes,
    });
  };

  const isValid = reminderDate !== undefined; // Sadece tarih zorunlu

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {initialData ? 'Erinnerung bearbeiten' : 'Neue Erinnerung'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Title - Artık opsiyonel */}
          <div className="space-y-2">
            <Label htmlFor="title">{t('common:x3.title2', 'Titel')}</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('common:x3.egCallCustomer', 'z.B. Kunde zurückrufen (optional)')}
              maxLength={255}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t('common:field.description', 'Beschreibung')}</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('common:x3.optionalDetails', 'Optionale Details...')}
              rows={3}
              maxLength={2000}
            />
          </div>

          {/* Date Picker - SADECE BU ZORUNLU */}
          <div className="space-y-2">
            <Label>{t('common:x3.dateReq', 'Datum *')}</Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {reminderDate ? (
                    format(reminderDate, 'PPP')
                  ) : (
                    <span>{t('common:x3.selectDate', 'Datum wählen')}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 data-[state=open]:animate-none data-[state=closed]:animate-none" align="start">
                <Calendar
                  mode="single"
                  selected={reminderDate}
                  onSelect={handleDateSelect}
                  weekStartsOn={1}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Urgency - HIDDEN (sabit değer: medium) */}
          <div className="hidden">
            <Select value={urgency} onValueChange={(value) => setUrgency(value as ReminderUrgency)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {URGENCY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: option.color }}
                      />
                      {(option as any).labelKey ? t((option as any).labelKey, option.label, (option as any).labelOpts) : option.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Color - HIDDEN (sabit değer: #3b82f6) */}
          <div className="hidden">
            <div className="flex gap-2 flex-wrap">
              {COLOR_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setColor(option.value)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    color === option.value
                      ? 'border-gray-900 scale-110'
                      : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: option.value }}
                  title={(option as any).labelKey ? t((option as any).labelKey, option.label, (option as any).labelOpts) : option.label}
                />
              ))}
            </div>
          </div>

          {/* Notification - HIDDEN (sabit değer: 15) */}
          <div className="hidden">
            <Select
              value={notifyBeforeMinutes.toString()}
              onValueChange={(value) => setNotifyBeforeMinutes(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NOTIFY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {(option as any).labelKey ? t((option as any).labelKey, option.label, (option as any).labelOpts) : option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t('common:action.cancel', 'Abbrechen')}
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isLoading}>
            {isLoading ? 'Wird gespeichert...' : initialData ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
